import { savePreviewImage, updateCameraStationPreviewImage } from "camera/modules/cloud/media";

const INTERVAL = isDev ? 120000 : 30000;
const DEBUG_PREVIEW = false;
const RETRY_DELAY = 2000;

export default class PreviewUpdater {
  private intervalRef: NodeJS.Timeout | null = null;
  private retryTimeout: NodeJS.Timeout | null = null;
  private retryAttempted = false;
  private imageFile: CameraStationPreview["image"];
  private notifyViewers: Cb;

  constructor(notifyViewers: Cb) {
    this.notifyViewers = notifyViewers;
  }

  startTakingVideoPreviews = async () => {
    this.intervalRef = setInterval(this.uploadVideoPreview, INTERVAL);
  };

  stopTakingVideoPreviews = () => {
    if (this.intervalRef) clearInterval(this.intervalRef);
    this.clearRetry();
  };

  uploadVideoPreview = async ({ notify } = { notify: true }) => {
    try {
      log.mediaPreview("Taking video preview");
      const { base64 } = this.create();
      await this.upload(base64, { notify });
    } catch (e) {
      log.err("Error taking video preview", e);
    }
  };
  base64ToUint8Array = (base64: string) => {
    const byteCharacters = atob(base64);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    return new Uint8Array(byteNumbers);
  };
  getDetectionZonesPreview = async () => {
    const { base64, width, height } = this.create();
    const uint8Array = this.base64ToUint8Array(base64);
    return {
      imageWidth: width,
      imageHeight: height,
      imageData: uint8Array
    } as const;
  };

  getPreviewImagePayload = () => this.imageFile;
  restartInterval = () => {
    this.stopTakingVideoPreviews();
    this.startTakingVideoPreviews();
  };

  private create = () => {
    const video = document.querySelector<HTMLVideoElement>("#camera-video");
    if (!video) {
      throw Error("No video element found");
    }
    const canvas = window.document.createElement("canvas");
    const { videoWidth, videoHeight } = video;
    canvas.width = videoWidth;
    canvas.height = videoHeight;
    const context = canvas.getContext("2d");

    if (!context) {
      throw Error("Error getting canvas context");
    }

    context.drawImage(video, 0, 0);
    const dataUrl = canvas.toDataURL("image/jpeg");

    if (DEBUG_PREVIEW) {
      canvas.style.position = "fixed";
      canvas.style.bottom = "0%";
      canvas.style.left = "50%";
      canvas.style.transform = "translateX(-50%)";
      document.body.appendChild(canvas);
    }

    const base64 = dataUrl.split(",")[1];

    canvas.remove();

    return { base64, width: videoWidth, height: videoHeight };
  };

  private upload = async (base64: string, { notify }: { notify: boolean }) => {
    if (!base64) {
      log.err("No base64 image to upload", base64);
      if (!this.retryAttempted) {
        this.retryAttempted = true;
        this.clearRetry(true);
        this.retryTimeout = setTimeout(() => this.uploadVideoPreview({ notify }), RETRY_DELAY);
        return;
      }

      if (!isDev) restartApp({ cause: "preview-image-missing" });
      return;
    }

    this.clearRetry();

    const imageFile = await savePreviewImage({ base64 });
    if (!imageFile) return;

    await updateCameraStationPreviewImage({ imageFile });
    this.imageFile = { fileName: imageFile.name, url: imageFile.url };
    if (notify) this.notifyViewers();
  };

  private clearRetry = (preserveAttempt = false) => {
    if (this.retryTimeout) clearTimeout(this.retryTimeout);
    this.retryTimeout = null;
    if (!preserveAttempt) this.retryAttempted = false;
  };
}
